import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, Children, isValidElement, cloneElement } from 'react';
import { Box } from '../Box/index.js';
import { Stack } from '../Stack/index.js';
import { Icon } from '../Icon/index.js';

const ListItemIcon = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    color,
    name,
    ...rest
  } = _ref;
  return /*#__PURE__*/React__default.createElement(Icon, _extends({
    ref: forwardedRef,
    color: color,
    mr: "space30",
    name: name,
    size: "1em"
  }, rest));
});
/**
 * ListItem is a list item primitive. It renders a `<li>` element by default.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/list#usage)
 * - [Props](https://blockparty.endpoint.com/components/list#props)
 * <br />
 *
 */
const ListItem = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    children,
    color,
    iconName,
    dataTestId = 'list-item',
    ...rest
  } = _ref2;
  return /*#__PURE__*/React__default.createElement(Box, _extends({
    ref: forwardedRef,
    as: "li",
    "data-test-id": dataTestId,
    position: "relative"
  }, rest), iconName && /*#__PURE__*/React__default.createElement(ListItemIcon, {
    color: color,
    name: iconName,
    verticalAlign: "bottom"
  }), children);
});
/**
 * List component is used to display list items. It renders a `<ul>` element by default.
 *
 * List extends from `Stack` with a `space20` spacing between each element.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/list#usage)
 * - [Props](https://blockparty.endpoint.com/components/list#props)
 * <br />
 *
 */
const List = /*#__PURE__*/forwardRef((_ref3, forwardedRef) => {
  let {
    children,
    spacing = 'space20',
    dataTestId = 'list',
    ...rest
  } = _ref3;
  return /*#__PURE__*/React__default.createElement(Stack, _extends({
    ref: forwardedRef,
    as: "ul",
    "data-test-id": dataTestId,
    listStylePosition: "inside",
    listStyleType: "disc",
    spacing: spacing
  }, rest), Children.map(children, (child, index) => {
    if (! /*#__PURE__*/isValidElement(child)) return;
    const isLast = index + 1 === Children.count(children);
    if (isLast) {
      return child;
    }
    return /*#__PURE__*/cloneElement(child, {
      spacing
    });
  }));
});

export { List, ListItem, ListItemIcon };
